// Firebase SDK initialization (singleton).
// All Firebase Auth calls in the app should import { auth } from here.
import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  setPersistence,
  browserLocalPersistence,
  GoogleAuthProvider,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export const firebaseApp =
  getApps()[0] || initializeApp(firebaseConfig);

// The iOS app runs from capacitor://localhost (WKWebView can't serve the app
// over https). getAuth() also sets up the popup/redirect resolver, which loads
// an iframe from authDomain that never initialises on that origin — and every
// sign-in call waits for it, so they hang forever. The native app never uses
// popups (Google/Apple go through the native sheets), so on iOS initialise
// Auth without the resolver, as Firebase recommends for Capacitor.
// Android (https://localhost) and the web keep getAuth() unchanged.
const isNativeIOS =
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

export const auth = isNativeIOS
  ? initializeAuth(firebaseApp, { persistence: indexedDBLocalPersistence })
  : getAuth(firebaseApp);

if (!isNativeIOS) {
  // Sessions persist across reloads/tabs (matches old localStorage behaviour).
  setPersistence(auth, browserLocalPersistence).catch((e) => {
    // Non-fatal: persistence will fall back to in-memory.
    console.warn('Firebase auth persistence failed:', e?.message);
  });
}

export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });
